/**
 * The Home screen: Personal Travel Workbench.
 *
 * Designed per docs/TripPool_UI_UX_Design_Specification_v0.2.md §4.1:
 * 1. Hero Universal Capture Bar: Paste URL or place name right on home.
 * 2. Trip Trigger Card: Bridge from accumulated saved places to an actionable trip.
 * 3. Recent Saves / Curation: Immediate recall and quick enrichment.
 *
 * **Every number on this screen is a real count.** A first version of the restyle drew a stat bar and an
 * itinerary snapshot with hard-coded figures — `3` itineraries, `2` companions, a named trip, `8/15`
 * places arranged, three day cards — so an account with nothing saved was shown a populated timeline of
 * trips that did not exist. The counts that survive are read from the two queries below (saved places and
 * trips), the elements with no data behind them are gone, and nothing here invents a value to fill a slot.
 *
 * **And every word is in `copy.ts`.** The same restyle replaced `{copy.home.tripTriggerDesc}` and
 * `{copy.home.tripTriggerAction}` with literals and added about twenty-five more, which is what makes the
 * screen untranslatable. The owner's rule is absolute: no user-facing string is written at its call site.
 */

import { useMemo, useState } from "react";
import type { ReactElement } from "react";
import { Link, useNavigate } from "react-router-dom";

import { categoryGlyph, copy } from "../copy.ts";
import { CaptureBar } from "../components/CaptureBar.tsx";
import { isUsableCoordinate } from "../lib/geo.ts";
import { usePlaces, useTrips } from "../lib/queries.ts";
import { useCurrentUser } from "../lib/session-context.tsx";

function getGreeting(displayName?: string | null): string {
  const hour = new Date().getHours();
  const name = displayName ?? copy.home.greetingNameFallback;
  if (hour >= 4 && hour < 12) return copy.home.greetingMorning(name);
  if (hour >= 12 && hour < 18) return copy.home.greetingAfternoon(name);
  return copy.home.greetingEvening(name);
}

export function HomeScreen(): ReactElement {
  const user = useCurrentUser();
  const userId = user?.id ?? "";
  const navigate = useNavigate();

  const placesQuery = usePlaces(userId, { limit: 100 });
  const tripsQuery = useTrips(userId);

  /*
   * Memoised on the query's own data, not written inline.
   *
   * `allPlaces` is a dependency of three `useMemo`s below; rebuilt as `data?.items ?? []` on every render,
   * its identity changed every time and each of those memos recomputed for nothing. Tying it to the query
   * result — which React Query keeps referentially stable between fetches — makes the dependency mean what
   * it says.
   */
  const allPlaces = useMemo(() => placesQuery.data?.items ?? [], [placesQuery.data]);
  const placesPageComplete = placesQuery.data !== undefined && placesQuery.data.next_cursor === null;
  const recentPlaces = allPlaces.slice(0, 6);

  /*
   * The itinerary stat, from the trips the user actually has.
   *
   * `useTrips` is one more request on this screen, and it is the price of the number being true: the
   * alternative is the three the fabricated version printed for everybody.
   */
  const tripCount = tripsQuery.data?.items.length ?? 0;

  const [selectedAreaId, setSelectedAreaId] = useState<string>("");

  const areasList = useMemo(() => {
    const map = new Map<string, { id: string; name: string; count: number }>();
    for (const place of allPlaces) {
      for (const area of place.areas) {
        const item = map.get(area.id);
        if (item) item.count += 1;
        else map.set(area.id, { id: area.id, name: area.name, count: 1 });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [allPlaces]);

  const displayedPlaces = useMemo(() => {
    if (selectedAreaId === "") return recentPlaces;
    return allPlaces.filter((p) => p.areas.some((a) => a.id === selectedAreaId)).slice(0, 6);
  }, [allPlaces, recentPlaces, selectedAreaId]);

  const triggerArea = useMemo(() => {
    if (!placesPageComplete) return null;
    const counts = new Map<string, { id: string; name: string; count: number }>();
    for (const place of allPlaces) {
      for (const area of place.areas) {
        const entry = counts.get(area.id);
        if (entry === undefined) counts.set(area.id, { id: area.id, name: area.name, count: 1 });
        else entry.count += 1;
      }
    }
    return [...counts.values()].filter((area) => area.count >= 3).sort((a, b) => b.count - a.count)[0] ?? null;
  }, [allPlaces, placesPageComplete]);

  const trigger =
    triggerArea !== null
      ? {
          areaId: triggerArea.id,
          label: triggerArea.name,
          count: triggerArea.count,
          href: `/trips/new?areaId=${triggerArea.id}`,
        }
      : null;

  return (
    <div className="home">
      {/* Header Greeting */}
      <header className="home__header">
        <div className="home__header-main">
          <h1 className="home__greeting">{getGreeting(user?.display_name)}</h1>
          <p className="home__tagline">{copy.app.tagline}</p>
        </div>

        {/*
          The counts, both read from a query: the saved places this screen already loads, and the trips
          from `useTrips`. The Companion slot is deliberately absent — nothing in the product models a
          travel party at account level, and the version that filled the slot with `2` was a decoration
          wearing a number.
        */}
        <div className="home__stats" aria-label={copy.home.statsLabel}>
          <div className="home__stat">
            <span className="home__stat-icon material-symbols-outlined" aria-hidden="true">bookmark</span>
            <div className="home__stat-info">
              <span className="home__stat-value">{allPlaces.length}</span>
              <span className="home__stat-label">{copy.home.statSaved}</span>
            </div>
          </div>
          <div className="home__stat-divider" aria-hidden="true" />
          <div className="home__stat">
            <span className="home__stat-icon material-symbols-outlined" aria-hidden="true">flight_takeoff</span>
            <div className="home__stat-info">
              <span className="home__stat-value">{tripCount}</span>
              <span className="home__stat-label">{copy.home.statTrips}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Top Bento Zone: Dual Split Hero Section */}
      <div className="home__bento-top">
        {/* Left Hero Card: Universal Quick Save */}
        <section className="home__hero" aria-labelledby="home-hero-title">
          <div className="home__hero-head">
            <div className="home__hero-title-row">
              <h2 id="home-hero-title" className="home__hero-title">
                {copy.home.heroTitle}
              </h2>
              <span className="home__hero-sparkle material-symbols-outlined" aria-hidden="true">auto_fix_high</span>
            </div>
            <p className="home__hero-intro">{copy.home.heroIntro}</p>
          </div>

          <CaptureBar />

          {/*
            The sources the bar accepts. The platform names are the products they name, so they are listed
            in `copy.ts` rather than spelled here: a translator keeps "TikTok" as it is, but the list itself
            and the label over it are copy, and the alternative is a second place the screen's words live.
          */}
          <div className="home__hero-sources">
            <span className="home__hero-sources-label">{copy.home.heroSourcesLabel}</span>
            {copy.home.heroSources.map((source) => (
              <span key={source} className="home__hero-source-chip">
                <span className="home__hero-source-dot" aria-hidden="true" /> {source}
              </span>
            ))}
          </div>
        </section>

        {/* Right Hero Card: Smart Itinerary Concierge Trigger */}
        {trigger !== null && (
          <section className="home__trigger-card" aria-label={copy.home.tripTriggerBadge}>
            <div className="home__trigger-content">
              <div className="home__trigger-badge">
                <span className="home__trigger-badge-icon material-symbols-outlined" aria-hidden="true">lightbulb</span>
                <span>{copy.home.tripTriggerBadge}</span>
              </div>
              <h2 className="home__trigger-title">
                {copy.home.tripTriggerTitle(trigger.label, trigger.count)}
              </h2>
              <p className="home__trigger-meta">
                {copy.home.tripTriggerMeta(trigger.count)}
              </p>
              <p className="home__trigger-desc">{copy.home.tripTriggerDesc}</p>

              {/*
                The places the suggestion is about, as their category glyphs — the same tinted-square idea
                the list and the planner use. They were remote stock photographs, one per category, with a
                random `picsum` fallback: an image the API never returned, standing in for a place it does
                not depict, which ADR-004 rules out and the contract has no field for.
              */}
              <div className="home__trigger-avatars">
                <div className="home__trigger-avatar-stack">
                  {allPlaces
                    .filter((place) => place.areas.some((area) => area.id === trigger.areaId))
                    .slice(0, 3)
                    .map((place) => (
                      <span key={place.id} className="home__trigger-avatar" aria-hidden="true">
                        {categoryGlyph(place.categories).glyph}
                      </span>
                    ))}
                  {trigger.count > 3 && (
                    <span className="home__trigger-avatar-more">+{trigger.count - 3}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="home__trigger-actions">
              <button
                type="button"
                className="button button--primary home__trigger-action"
                onClick={() => navigate(trigger.href)}
              >
                <span>{copy.home.tripTriggerAction}</span>
                <span className="home__trigger-action-icon material-symbols-outlined" aria-hidden="true">arrow_forward</span>
              </button>
              <button
                type="button"
                className="home__trigger-dismiss"
                onClick={() => navigate("/places")}
              >
                {copy.home.tripTriggerDismiss}
              </button>
            </div>
          </section>
        )}
      </div>

      {/* Recent Saves & Curation Surface */}
      {allPlaces.length > 0 && (
        <section className="home__section" aria-labelledby="recentTitle">
          <div className="home__section-head">
            <div className="home__section-lead">
              <h2 id="recentTitle" className="home__section-title">
                {copy.home.recentSavesTitle}
              </h2>
              <span className="home__section-dot" aria-hidden="true" />
              <span className="home__section-status">{copy.home.recentSavesSync}</span>
            </div>
            <Link to="/places" className="link home__section-link">
              <span>{copy.home.viewAllPlacesCount(allPlaces.length)}</span>
              <span className="home__section-link-icon material-symbols-outlined" aria-hidden="true">arrow_forward</span>
            </Link>
          </div>

          {/*
            City filter chips.

            `role="tablist"` with `role="tab"` and `aria-selected` on each button, which is the pattern the
            trips screen already uses. The first version set the tablist on the row and left the buttons as
            plain buttons, so a screen reader was told a tab list existed and then heard no tabs in it.
          */}
          {areasList.length > 0 && (
            <div className="home__filters" role="tablist" aria-label={copy.home.filtersLabel}>
              <button
                type="button"
                role="tab"
                aria-selected={selectedAreaId === ""}
                className={selectedAreaId === "" ? "home__filter-chip home__filter-chip--on" : "home__filter-chip"}
                onClick={() => setSelectedAreaId("")}
              >
                {copy.home.filterAllCount(allPlaces.length)}
              </button>
              {areasList.map((area) => {
                const active = selectedAreaId === area.id;
                return (
                  <button
                    key={area.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={active ? "home__filter-chip home__filter-chip--on" : "home__filter-chip"}
                    onClick={() => setSelectedAreaId(area.id)}
                  >
                    {copy.home.filterArea(area.name, area.count)}
                  </button>
                );
              })}
            </div>
          )}

          <ul className="home__recent-list">
            {displayedPlaces.map((place) => (
              <li key={place.id} className="home__recent-item">
                <Link to={`/places/${place.id}`} className="home__recent-link">
                  <div className="home__recent-thumb">
                    <span className="home__recent-glyph" aria-hidden="true">
                      {categoryGlyph(place.categories).glyph}
                    </span>
                  </div>
                  <div className="home__recent-info">
                    <span className="home__recent-name">{place.name}</span>
                    <span className="home__recent-addr">
                      {place.address ?? copy.home.noAddressYet}
                    </span>
                  </div>
                  {!isUsableCoordinate(place.location) ? (
                    <span className="home__badge-warning">
                      <span className="home__badge-icon material-symbols-outlined" aria-hidden="true">warning</span>
                      <span>{copy.home.badgeNoLocation}</span>
                    </span>
                  ) : (
                    <span className="home__badge-ready">
                      <span className="home__badge-icon material-symbols-outlined" aria-hidden="true">check</span>
                      <span>{copy.home.badgeLocated}</span>
                    </span>
                  )}
                  <span className="home__recent-add" aria-hidden="true">
                    <span className="home__recent-add-icon material-symbols-outlined">add_circle_outline</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        The map banner, only when there is something on the map.

        It is a door to `/places`, and with nothing saved it was a door to an empty screen offering "0
        địa điểm" over a blank preview — an element the app has no data for, drawn anyway. The capture hero
        above stays, because that is the action a user with nothing saved actually has.
      */}
      {allPlaces.length > 0 && (
        <section className="home__map-banner" aria-label={copy.home.mapBannerLabel}>
          <div className="home__map-banner-content">
            <span className="home__map-banner-tag">
              <span className="home__map-banner-tag-icon material-symbols-outlined" aria-hidden="true">map</span>
              <span>{copy.home.mapBannerLabel}</span>
            </span>
            <h3 className="home__map-banner-title">{copy.home.mapBannerTitle(allPlaces.length)}</h3>
            <p className="home__map-banner-desc">{copy.home.mapBannerDesc}</p>
            <button
              type="button"
              className="button home__map-banner-btn"
              onClick={() => navigate("/places")}
            >
              <span className="home__map-banner-btn-icon material-symbols-outlined" aria-hidden="true">near_me</span>
              <span>{copy.home.mapBannerAction}</span>
            </button>
          </div>
          <div className="home__map-banner-preview" aria-hidden="true">
            <span className="home__map-banner-pill">
              <span className="home__map-banner-pill-icon material-symbols-outlined" aria-hidden="true">pin_drop</span>
              {triggerArea ? (
                <span>{copy.home.mapBannerPinsAt(triggerArea.count, triggerArea.name)}</span>
              ) : (
                <span>{copy.home.mapBannerPins(allPlaces.length)}</span>
              )}
            </span>
          </div>
        </section>
      )}
    </div>
  );
}
